import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function GalleryLightbox({ images = [], startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose]);

  // touch swipe
  useEffect(() => {
    let startX = 0;
    const onStart = (e) => { startX = e.touches[0].clientX; };
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) { dx > 0 ? prev() : next(); }
    };
    window.addEventListener("touchstart", onStart);
    window.addEventListener("touchend", onEnd);
    return () => { window.removeEventListener("touchstart", onStart); window.removeEventListener("touchend", onEnd); };
  }, [prev, next]);

  if (!images.length) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center" onClick={onClose} data-testid="gallery-lightbox">
      <button onClick={onClose} data-testid="lightbox-close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white z-10">
        <X className="w-5 h-5" />
      </button>
      <button onClick={(e)=>{e.stopPropagation(); prev();}} data-testid="lightbox-prev" className="absolute left-4 md:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white z-10">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button onClick={(e)=>{e.stopPropagation(); next();}} data-testid="lightbox-next" className="absolute right-4 md:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white z-10">
        <ChevronRight className="w-6 h-6" />
      </button>
      <div className="max-w-[92vw] max-h-[85vh] flex items-center justify-center" onClick={(e)=>e.stopPropagation()}>
        <img src={images[idx]} alt={`gallery ${idx+1}`} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" data-testid="lightbox-image" />
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, i) => (
          <button key={i} onClick={(e)=>{e.stopPropagation(); setIdx(i);}} data-testid={`lightbox-dot-${i}`}
            className={`w-2.5 h-2.5 rounded-full transition-all ${i===idx?"bg-white w-6":"bg-white/40 hover:bg-white/60"}`} />
        ))}
      </div>
      <div className="absolute bottom-6 right-6 text-white/70 text-xs font-medium">
        {idx+1} / {images.length}
      </div>
    </div>
  );
}
